import { BaseDomain, ApiClient, tokenManager, requestManager, PaginationService } from '../shared';
import type { BaseApiError } from '../shared';
import type { AppointmentApi, PaginatedAppointmentsApi, Appointment, PaginatedAppointments, AppointmentListRequest } from '@/types/appointments';
import { AppointmentApiError } from '@/types/appointments';

const ENDPOINTS = {
  BASE: '/dietitians',
} as const;

/** Shape of an error thrown by the API client, as far as this domain reads it */
type ThrownApiError = Partial<Pick<BaseApiError, 'message' | 'status' | 'fieldErrors' | 'code'>>;

export class AppointmentDomain extends BaseDomain<AppointmentApiError> {
  constructor() {
    const apiClient = new ApiClient<AppointmentApiError>(
      (message: string, status?: number, fieldErrors?: Record<string, string>, code?: string) =>
        new AppointmentApiError(message, status, fieldErrors, code)
    );
    super(apiClient, tokenManager, requestManager);
  }

  protected getDomainName(): string {
    return 'Appointment';
  }

  protected createDomainError(
    operation: string,
    message: string,
    status?: number,
    fieldErrors?: Record<string, string>,
    code?: string
  ): AppointmentApiError {
    return new AppointmentApiError(message, status, fieldErrors, code);
  }

  async listAppointments(request: AppointmentListRequest): Promise<PaginatedAppointments> {
    return this.executeWithRetry(async () => {
      if (!request.dietitianId || !request.patientId) {
        throw this.createError('listAppointments', 'dietitianId and patientId are required', 400);
      }

      const normalizedPage = request.page ?? 1;
      const normalizedPageSize = request.pageSize ?? 10;
      const validation = PaginationService.validate({ page: normalizedPage, perPage: normalizedPageSize });
      if (!validation.isValid) {
        throw this.createError(
          'listAppointments',
          'Invalid pagination parameters',
          400,
          this.buildFieldErrorsFromValidation(validation.errors),
          'INVALID_PAGINATION'
        );
      }

      try {
        const query = new URLSearchParams({
          page: String(normalizedPage),
          page_size: String(normalizedPageSize),
        });

        const response = await this.apiClient.get<PaginatedAppointmentsApi>(
          `${ENDPOINTS.BASE}/${request.dietitianId}/patients/${request.patientId}/appointments?${query.toString()}`
        );
        return this.transformPaginatedAppointments(response);
      } catch (error) {
        throw this.handleApiError(error);
      }
    });
  }

  private transformPaginatedAppointments(payload: PaginatedAppointmentsApi): PaginatedAppointments {
    return {
      items: (payload.items || []).map(this.transformAppointmentFromApi),
      page: payload.page,
      pageSize: payload.page_size,
      totalItems: payload.total_items,
    };
  }

  private transformAppointmentFromApi = (appointment: AppointmentApi): Appointment => {
    return {
      id: appointment.id,
      patientId: appointment.patient_id,
      dietitianId: appointment.dietitian_id,
      scheduledAt: appointment.scheduled_at,
      status: appointment.status,
      type: appointment.type ?? null,
      location: appointment.location ?? null,
    };
  };

  private buildFieldErrorsFromValidation(errors: string[]): Record<string, string> {
    const fieldErrors: Record<string, string> = {};
    for (const error of errors) {
      if (error.includes('Page number')) {
        fieldErrors.page = error;
      } else if (error.includes('Page size')) {
        fieldErrors.perPage = error;
      } else {
        fieldErrors.general = error;
      }
    }
    return fieldErrors;
  }

  private handleApiError(error: unknown): AppointmentApiError {
    if (error instanceof AppointmentApiError) {
      return error;
    }

    const apiError = error as ThrownApiError | null | undefined;
    const message = apiError?.message || 'Failed to fetch appointments';
    const status = apiError?.status;
    const fieldErrors = apiError?.fieldErrors;
    const code = apiError?.code;

    return this.createError('listAppointments', message, status, fieldErrors, code);
  }
}

export const appointmentDomain = new AppointmentDomain();
